<template>
  <UModal
    :open="modelValue"
    :title="title"
    :close="showCloseButton"
    :content="contentProps"
    :ui="ui"
    fullscreen
    @update:open="onOpenChange"
    @after:leave="onAfterLeave"
  >
    <template
      v-if="$slots['title']"
      #title
    >
      <slot
        name="title"
        :title="title"
      />
    </template>

    <template
      v-if="$slots['description']"
      #description
    >
      <slot name="description" />
    </template>

    <template
      v-if="$slots['footer']"
      #footer
    >
      <slot name="footer" />
    </template>

    <template #body>
      <slot />
    </template>
  </UModal>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import UModal from '@nuxt/ui/components/Modal.vue'
import { useMediaQuery } from '~/composables/useMediaQuery'
import { useOverlayShell } from '~/composables/useOverlayShell'
import type { OverlayShellEvents } from '~/types/overlays/OverlayShellEvents'

interface Props {
  /** Controls whether the panel is open. */
  modelValue: boolean
  /** Panel title displayed in the header. */
  title: string
  /** Additional classes applied to the panel. */
  panelClass?: string
  /** Allows closing when the backdrop is clicked. */
  closeOnOutsideClick?: boolean
  /** Controls visibility of the close button. */
  showCloseButton?: boolean
  /** Closes the panel when the viewport reaches the desktop breakpoint. */
  mobileOnly?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  closeOnOutsideClick: true,
  mobileOnly: false,
  panelClass: '',
  showCloseButton: true
})

const emit = defineEmits<OverlayShellEvents>()

defineSlots<{
  default?: () => unknown
  footer?: () => unknown
  title?: (_props: { title: string }) => unknown
  description?: () => unknown
}>()

const ui = computed(() => ({ content: props.panelClass }))
const isDesktop = useMediaQuery('(min-width: 1024px)')

watch(
  () => [props.modelValue, isDesktop.value],
  ([isOpen, desktop]) => {
    if (props.mobileOnly && isOpen === true && desktop === true) emit('close')
  },
  { immediate: true }
)

const { onOpenChange, onAfterLeave, contentProps } = useOverlayShell({
  closeOnOutsideClick: () => props.closeOnOutsideClick,
  emit,
  isOpen: () => props.modelValue
})
</script>
